import axios from 'axios';
import { httpApi } from './http';
import type { Trip, TripListParams, TripListResult, TripRequest, TripRequestListParams, TripRequestListResult } from '../types/trip';

const api = httpApi;

const pickArray = (payload: any): any[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.trips)) return payload.trips;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.results)) return payload.results;
  if (payload.data && Array.isArray(payload.data.data)) return payload.data.data;
  return [];
};

const pickNumber = (payload: any, keys: string[], fallback: number) => {
  for (const k of keys) {
    const v = payload?.[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return fallback;
};

export class TripService {
  static async listTrips(params: TripListParams = {}): Promise<TripListResult> {
    try {
      const response = await api.post<any>('/trips', {
        type: params.type ?? '',
        name: params.name ?? '',
        sort: params.sort ?? '-createdAt',
        limit: params.limit ?? 50,
        page: params.page ?? 1,
      });

      const payload = response.data;
      if (!payload) throw new Error('No data received from server');
      if (payload.success === false) {
        throw new Error(payload.error || payload.message || 'API request failed');
      }

      const items = pickArray(payload) as Trip[];
      const total = pickNumber(payload, ['total', 'count', 'countTotal'], items.length);
      const page = pickNumber(payload, ['page'], params.page ?? 1);
      const pages = pickNumber(payload, ['pages', 'totalPages'], 1);

      return { items, total, page, pages };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const apiError = (error.response?.data as any)?.error || (error.response?.data as any)?.message;
        if (apiError) throw new Error(apiError);
        if (status) throw new Error(`Server error: ${status}`);
        if (error.request) throw new Error('Network error: Unable to connect to server');
      }
      throw new Error('Failed to fetch trips');
    }
  }

  static async getTripById(id: string): Promise<Trip> {
    try {
      const response = await api.get<any>(`/trips/${encodeURIComponent(id)}`);
      const payload = response.data;
      if (!payload) throw new Error('No data received from server');
      if (payload.success === false) {
        throw new Error(payload.error || payload.message || 'API request failed');
      }

      const trip = (payload.data || payload.trip || payload) as Trip;
      if (!trip || typeof trip !== 'object') throw new Error('Invalid trip payload');
      return trip;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const apiError = (error.response?.data as any)?.error || (error.response?.data as any)?.message;
        if (apiError) throw new Error(apiError);
        if (status) throw new Error(`Server error: ${status}`);
        if (error.request) throw new Error('Network error: Unable to connect to server');
      }
      throw new Error('Failed to fetch trip');
    }
  }

  static async listTripRequests(params: TripRequestListParams = {}): Promise<TripRequestListResult> {
    try {
      const response = await api.get<any>('/trips/request', {
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 50,
          sort: params.sort ?? '-createdAt',
          ...(params.search ? { search: params.search } : {}),
          ...(params.userId ? { userId: params.userId } : {}),
          ...(params.tripId ? { tripId: params.tripId } : {}),
          ...(params.tripName ? { tripName: params.tripName } : {}),
          ...(params.originAirport ? { originAirport: params.originAirport } : {}),
          ...(params.destinationAirport ? { destinationAirport: params.destinationAirport } : {}),
          ...(params.hotelType ? { hotelType: params.hotelType } : {}),
          ...(params.travelClass ? { travelClass: params.travelClass } : {}),
          ...(params.mobile ? { mobile: params.mobile } : {}),
          ...(params.rewardPoints ? { rewardPoints: params.rewardPoints } : {}),
          ...(params.dateFrom ? { dateFrom: params.dateFrom } : {}),
          ...(params.dateTo ? { dateTo: params.dateTo } : {}),
          ...(params.createdFrom ? { createdFrom: params.createdFrom } : {}),
          ...(params.createdTo ? { createdTo: params.createdTo } : {}),
        },
      });

      const payload = response.data;
      if (!payload) throw new Error('No data received from server');
      if (payload.success === false) {
        throw new Error(payload.error || payload.message || 'API request failed');
      }

      const items = pickArray(payload) as TripRequest[];
      const total = pickNumber(payload, ['total', 'count'], items.length);
      const page = pickNumber(payload, ['page'], params.page ?? 1);
      const pages = pickNumber(payload, ['pages', 'totalPages'], 1);

      return { items, total, page, pages };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const apiError = (error.response?.data as any)?.error || (error.response?.data as any)?.message;
        if (apiError) throw new Error(apiError);
        if (status) throw new Error(`Server error: ${status}`);
        if (error.request) throw new Error('Network error: Unable to connect to server');
      }
      throw new Error('Failed to fetch trip requests');
    }
  }

  static async getTripRequestById(id: string): Promise<TripRequest> {
    try {
      // Since there's no single-item endpoint, fetch from list and filter by ID
      // Use a high limit to ensure we get the item
      const response = await api.get<any>('/trips/request', {
        params: {
          page: 1,
          limit: 1000, // High limit to ensure we find the item
          sort: '-createdAt',
        },
      });

      const payload = response.data;
      if (!payload) throw new Error('No data received from server');
      if (payload.success === false) {
        throw new Error(payload.error || payload.message || 'API request failed');
      }

      const items = pickArray(payload) as TripRequest[];
      const tripRequest = items.find((item) => item._id === id);

      if (!tripRequest) {
        throw new Error(`Trip request with ID ${id} not found`);
      }

      return tripRequest;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const apiError = (error.response?.data as any)?.error || (error.response?.data as any)?.message;
        if (apiError) throw new Error(apiError);
        if (status) throw new Error(`Server error: ${status}`);
        if (error.request) throw new Error('Network error: Unable to connect to server');
      }
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to fetch trip request');
    }
  }
}
